import React from 'react';
import styled, { css } from 'styled-components';

import {
  BUTTON_ROLES,
  BUTTON_THEMES,
  BUTTON_TYPES,
} from '../../../shared/constants';

import { DEQ_TYPES } from '../../../utils/deq';
import {
  getElementAction,
  getLinkAttributes,
  getToLink,
  renderContent,
} from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';

interface Props {
  target?: string;
  textAlign?: string;
  type: 'internal' | 'external' | 'file' | 'submit';
  label: string;
  to?: string;
  className?: string;
  closeMenu?: (parameter: string) => void;
  iconPosition?: 'start' | 'end';
  icon: 'none' | 'mail' | 'default' | 'chevron' | 'arrow';
  disabled?: boolean;
  deqComponentTitle?: string;
  onButtonClick?: (...params: any[]) => void;
  customOnClick?: () => void;
  showModal?: boolean;
  deqCustomLabel?: string;
  deqCustomType?: string;
  deqCustomAttributes?: { [key: string]: any };
  buttonTheme?: 'default' | 'highlight' | 'muted' | 'choiceButton';
  buttonAction?: 'submit' | 'reset' | 'button';
}

interface StyledComponentProps {
  themeType: string;
  disabled?: boolean;
  textAlign?: string;
}

const PrimaryButton = ({
  textAlign = 'center',
  type = 'internal',
  label,
  to,
  className,
  iconPosition = 'end',
  icon,
  disabled,
  deqComponentTitle = undefined,
  closeMenu,
  buttonTheme = BUTTON_THEMES.default,
  onButtonClick,
  customOnClick,
  showModal = false,
  deqCustomType,
  deqCustomLabel,
  deqCustomAttributes = {},
  buttonAction,
  target = '_self',
}: Props) => {
  const closeMenuTrackEvent = (link: string) => {
    if (closeMenu) {
      closeMenu(link);
    }
  };
  const isCustomClick = !!(onButtonClick || customOnClick) && !to;
  const attributes = to && getLinkAttributes(showModal, to, type, target);
  const trackingAction = type ? getElementAction(type) : undefined;
  const trackingButtonTarget = `${
    (to as unknown as { slug: string })?.slug ||
    to ||
    deqCustomAttributes?.elementTarget
  }`;

  const optionalTargetTracking = !isCustomClick
    ? {
        'data-element-target': trackingButtonTarget,
      }
    : {};

  return buttonAction === BUTTON_TYPES.submit ? (
    <SubmitButton
      data-testid="primary-button-submit"
      textAlign={textAlign}
      onClick={(event) => {
        if (to) closeMenuTrackEvent(to);
        if (onButtonClick) {
          onButtonClick(to, type, event);
        }
        if (customOnClick) customOnClick();
      }}
      className={className}
      themeType={buttonTheme}
      disabled={disabled}
      type={buttonAction}
      target={target}
      role={BUTTON_ROLES[type] || BUTTON_ROLES.internal}
      data-element-label={deqCustomLabel?.toLowerCase() || label?.toLowerCase()}
      {...optionalTargetTracking}
      data-element-type={deqCustomAttributes?.elementType || DEQ_TYPES.btnLink}
      data-element-style={BUTTON_TYPES.primary}
      data-component-title={deqComponentTitle?.toLowerCase()}
      data-component-type={
        deqCustomType ||
        deqCustomAttributes?.componentType ||
        DEQ_TYPES.primaryAnkeiler
      }
      {...attributes}
    >
      {renderContent(iconPosition, icon, type, label, BUTTON_TYPES.primary)}
    </SubmitButton>
  ) : (
    <StyledLink
      data-testid="primary-button-link"
      onClick={() => {
        if (to) closeMenuTrackEvent(to);
        if (onButtonClick) {
          onButtonClick(to, type);
        }
        if (customOnClick) customOnClick();
      }}
      target={target}
      href={to && (getToLink(showModal, to, type) as any)}
      className={className}
      themeType={buttonTheme}
      disabled={disabled}
      type={buttonAction}
      role={
        (showModal && BUTTON_ROLES.submit) ||
        BUTTON_ROLES[type] ||
        BUTTON_ROLES.internal
      }
      data-element-label={deqCustomLabel?.toLowerCase() || label?.toLowerCase()}
      {...optionalTargetTracking}
      data-element-type={deqCustomAttributes?.elementType || DEQ_TYPES.btnLink}
      data-element-style={BUTTON_TYPES.primary}
      data-element-action={buttonAction || trackingAction}
      data-component-title={deqComponentTitle?.toLowerCase()}
      data-component-type={
        deqCustomType ||
        deqCustomAttributes?.componentType ||
        DEQ_TYPES.primaryAnkeiler
      }
      textAlign={textAlign}
      {...attributes}
    >
      {renderContent(iconPosition, icon, type, label, BUTTON_TYPES.primary)}
    </StyledLink>
  );
};

export default PrimaryButton;

const primaryButtonStyle = css<StyledComponentProps>`
  align-items: center;
  background-color: ${({ theme, themeType, disabled }) =>
    disabled
      ? colors.disabledButtonGrey
      : theme.primaryButton[themeType || 'default'].backgroundColor};
  border: 1px solid
    ${({ theme, themeType, disabled }) =>
      disabled
        ? colors.disabledButtonGrey
        : theme.primaryButton[themeType || 'default'].borderColor};
  border-radius: ${({ theme }) => theme.radiuses.S};
  color: ${({ theme, themeType }) =>
    theme.primaryButton[themeType || 'default'].color} !important;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  font-family: ${({ theme, themeType }) =>
    theme.primaryButton[themeType || 'default'].fontFamily};
  font-weight: ${({ theme, themeType }) =>
    theme.primaryButton[themeType || 'default'].fontWeight};
  justify-content: ${(props) => props.textAlign};
  line-height: 24px;
  padding: ${({ theme, themeType }) =>
    theme.primaryButton[themeType || 'default'].padding};
  text-decoration: none;
  transition: background-color 0.15s ease;

  > svg {
    flex-shrink: 0;
    transition: transform 0.15s ease;
  }
  > svg.start {
    margin-right: 9px;
  }
  > svg.end {
    margin-left: 4px;
  }

  &:hover:not(&:disabled) {
    background-color: ${({ theme, themeType }) =>
      theme.primaryButton[themeType || 'default'].hoverBackgroundColor};
    border: 1px solid
      ${({ theme, themeType }) =>
        theme.primaryButton[themeType || 'default'].hoverBorderColor};
    color: ${({ theme, themeType }) =>
      theme.primaryButton[themeType || 'default'].hoverColor} !important;

    > svg.animate-on-hover {
      transform: translateX(4px);
    }
  }

  &:active:not(&:disabled),
  &:focus:not(&:disabled) {
    background-color: ${({ theme, themeType }) =>
      theme.primaryButton[themeType || 'default'].activeBackgroundColor};
  }

  &:focus:not(&:disabled) {
    color: ${({ theme, themeType }) =>
      theme.primaryButton[themeType || 'default'].focusColor} !important;
  }

  &:active:not(&:disabled) {
    color: ${({ theme, themeType }) =>
      theme.primaryButton[themeType || 'default'].activeColor} !important;
  }

  ${mq.to.breakpoint.S`
    display: flex;
  `}
`;

const SubmitButton = styled.button<StyledComponentProps>`
  ${primaryButtonStyle}
`;

const StyledLink = styled.a<StyledComponentProps>`
  ${primaryButtonStyle}
`;
